import dayjs from 'dayjs';
import { CATALOG, SEED_CUSTOMERS, type Customer, type PaymentMethod, type Refund as PosRefund } from '../POS/data';
import { DEFAULT_CONFIG, policyOf, seedPackages, TREATMENT_SERVICES, type TreatmentCard, type TreatmentPackage } from '../Treatment/data';
import { usageItems } from '../Treatment/logic';
import { DEFAULT_RULES } from './ktBooks';
import { allocateDiscount, MACHINES, refundQuoteOf, syncCards, usageNo } from './ktCards';
import { SURCHARGE } from './ktCounter';
import { billFromOrder, POS_BRANCH, type PosData } from './ktPos';
import {
  billNo, flagInvoice, invoiceNo, pendingTxns, ymd,
  type Bill, type BankRow, type BillDraft, type CaseStep, type CommissionRate, type InputInvoice, type KtState, type PayMethod, type PayooRow, type RefundCase, type Service, type Shift, type TaxClass, type TaxRate, type Team, type Voucher,
} from './ktLogic';

/**
 * Dữ liệu đầu cho demo: đơn POS thật trên máy + các tình huống SRS nêu mà dữ liệu POS chưa có (cọc, thiếu mã, chưa đăng ký...).
 * Thuế suất và tên đăng ký là giả định để demo, chờ Kế toán trưởng xác nhận.
 */

const svc = (id: string, name: string, code: string, type: Service['type'], taxClass: TaxClass, group: string, price: number, registeredName = name, bom?: Service['bom']): Service =>
  ({ id, name, code, type, taxClass, group, price, registeredName, bom });

/** Tên đăng ký Sở Y tế riêng cho một số mã POS để thấy cảnh báo lệch tên và chặn chưa đăng ký */
const REGISTERED: Record<string, string> = { 'DV-HIFU': 'Dịch vụ nâng cơ bằng sóng siêu âm hội tụ', 'DV-TAMTRANG': '' };
const POS_BOM: Record<string, Service['bom']> = { 'DV-THERMAGE': [{ code: 'VT-TIP-THERMAGE', qty: 1 }], 'DV-HIFU': [{ code: 'VT-CART-HIFU', qty: 1 }], 'DV-LASER': [{ code: 'VT-GEL', qty: 1 }] };

export const DEMO_SERVICES: Service[] = [
  svc('sv-hifu', 'Hifu nâng cơ', 'DV-HIFU-M', 'service', 'cosmetic', 'Nâng cơ', 18_000_000, 'Dịch vụ nâng cơ bằng sóng siêu âm hội tụ'),
  svc('sv-thermage', 'Thermage FLX', 'DV-THERMAGE-M', 'service', 'cosmetic', 'Trẻ hóa da', 45_000_000),
  svc('sv-laser', 'Laser trị nám', 'DV-LASERNAM', 'service', 'cosmetic', 'Laser', 6_500_000),
  svc('sv-filler', 'Filler khuyến mãi tháng 11', 'DV-FILLER11', 'service', 'cosmetic', 'Tiêm', 9_000_000, ''),
  svc('sv-kham', 'Khám da liễu', 'DV-KHAM', 'service', 'medical', 'Khám', 500_000),
  svc('sv-serum', 'Serum phục hồi', 'SP-SERUMPH', 'product', 'retail', 'Mỹ phẩm', 1_500_000),
  svc('sv-card', 'Thẻ Thermage 3 buổi', 'GOI-THERMAGE3', 'package', 'cosmetic', 'Trẻ hóa da', 45_000_000),
  // Phụ thu độc lập với đơn chính; thuế suất từng khoản là giả định, chờ Kế toán trưởng
  svc(SURCHARGE.pain.serviceId, SURCHARGE.pain.label, SURCHARGE.pain.serviceId, 'service', 'cosmetic', 'Phụ thu', 0),
  svc(SURCHARGE.test.serviceId, SURCHARGE.test.label, SURCHARGE.test.serviceId, 'service', 'medical', 'Phụ thu', 0),
  svc(SURCHARGE.installment.serviceId, SURCHARGE.installment.label, SURCHARGE.installment.serviceId, 'service', 'cosmetic', 'Phụ thu', 0),
  svc(SURCHARGE.vat.serviceId, SURCHARGE.vat.label, SURCHARGE.vat.serviceId, 'service', 'cosmetic', 'Phụ thu', 0),
  svc(SURCHARGE.other.serviceId, SURCHARGE.other.label, SURCHARGE.other.serviceId, 'service', 'cosmetic', 'Phụ thu', 0),
];

const POS_SERVICES: Service[] = CATALOG.map(c => {
  const taxClass: TaxClass = c.type === 'product' ? 'retail' : 'cosmetic';
  const registeredName = c.code in REGISTERED ? REGISTERED[c.code] : c.name;
  return svc(c.code, c.name, c.code, c.type, taxClass, c.group, c.price, registeredName, POS_BOM[c.code]);
});

/** Dịch vụ chỉ làm theo liệu trình, không bán lẻ trên POS */
const CARD_ONLY_SERVICES: Service[] = TREATMENT_SERVICES.filter(t => !CATALOG.some(c => c.code === t.code))
  .map(t => svc(t.code, t.name, t.code, 'service', 'cosmetic', t.group, t.price));

export const ALL_DEMO_SERVICES = [...DEMO_SERVICES, ...POS_SERVICES, ...CARD_ONLY_SERVICES];

/** Tỷ lệ hoa hồng, tua: giả định để demo, Kế toán trưởng sửa ở màn Hoa hồng và tua */
export const DEMO_COMMISSION_RATES: CommissionRate[] = [
  { group: '*', sale: 5, cskh: 1, doctor: 3, assistant: 1, nurse: 0.5, ktv: 1 },
  { group: 'Chăm sóc da', sale: 3, cskh: 1, doctor: 0, assistant: 0, nurse: 0, ktv: 3 },
  { group: 'Mỹ phẩm', sale: 2, cskh: 0.5, doctor: 0, assistant: 0, nurse: 0, ktv: 0 },
];

const TEAM_A: Team = { doctor: 'BS. Demo An', assistant: 'Phụ mổ Demo Bình', nurse: 'ĐD. Demo Chi', ktv: 'KTV Demo Dũng' };
const TEAM_B: Team = { doctor: 'BS. Demo Khoa', nurse: 'ĐD. Demo Chi', ktv: 'KTV Demo Hương' };
const SALES = ['Sale Demo Em', 'Sale Demo Giang'];
const CSKH = 'CSKH Demo Hà';

const NOTE_8 = 'Thuế suất giảm đến hết 31/12/2026';
/** Thẩm mỹ, bán lẻ: 8% đến hết 2026, 10% từ 01/01/2027; dịch vụ y tế giả định không chịu thuế */
export const ratesFor = (services: Service[]): TaxRate[] => services.flatMap<TaxRate>(sv => (sv.taxClass === 'medical'
  ? [{ id: `r-${sv.id}`, serviceId: sv.id, rate: null, from: '2025-01-01', note: 'Giả định để demo, chờ Kế toán trưởng xác nhận' }]
  : [
    { id: `r-${sv.id}-8`, serviceId: sv.id, rate: 8, from: '2025-07-01', note: NOTE_8 },
    { id: `r-${sv.id}-10`, serviceId: sv.id, rate: 10, from: '2027-01-01', note: 'Tự chuyển từ 01/01/2027' },
  ]));

const DEMO_STOCK: Record<string, number> = {
  'SP-SERUMB5': 180, 'SP-KCN': 96, 'SP-SRM': 1, 'SP-MASK': 40, 'SP-SERUMPH': 25,
  'VT-TIP-THERMAGE': 0, 'VT-CART-HIFU': 12, 'VT-GEL': 60,
};

interface Seed {
  day: number; hour: number; customer: string; phone?: string; kind?: Bill['kind']; service: string; gross: number;
  pay: [PayMethod, string?][]; cancelled?: string; edited?: boolean; depositOf?: number; issued?: boolean; by?: string; buyer?: Bill['buyer']; flag?: string;
  /** Khách mới trả một phần: còn lại là nợ */
  paid?: number;
  program?: Bill['program'];
  /** Nhiều dòng có chiết khấu đã bóc tách */
  lines?: { serviceId: string; name: string; list: number; lineDiscount: number; orderDiscount: number }[];
  /** Thu nhiều hình thức trên một bill: [hình thức, số tiền, mã] */
  parts?: [PayMethod, number, string?][];
  parent?: number;
}

/** Thẻ gắn với bill tình huống: [mã dịch vụ bên Liệu trình, số buổi, giá trị 1 buổi] */
interface CardSeed {
  bill: number; pkg: string; name: string; expiresIn: number; lines: [string, number, number][];
  uses: { day: number; items: [string, number][]; team: Team; machine?: string }[];
}

export interface DemoCardInput { treatment?: TreatmentCard[]; packages?: TreatmentPackage[]; refunds?: PosRefund[]; customers?: Customer[] }

const COMPANY_BUYER = { name: 'Công ty TNHH Demo Sao Mai', taxCode: '0312345678', address: '12 Đường Demo, Quận 1, TP.HCM', email: 'ketoan@demo-saomai.test' };

export function demoState(now: number, pos: PosData = { orders: [], transactions: [] }, cardInput: DemoCardInput = {}): KtState {
  const today = dayjs(now).startOf('day');
  const dateOf = (back: number) => ymd(today.subtract(back, 'day').valueOf());
  const py = (back: number, n: number) => `PY${today.subtract(back, 'day').format('YYMMDD')}${String(n).padStart(4, '0')}`;
  const ft = (back: number, n: number) => `FT${today.subtract(back, 'day').format('YYMMDD')}${String(n).padStart(5, '0')}`;

  const seeds: Seed[] = [
    { day: 2, hour: 9, customer: 'Chị Mai Demo', phone: '0900000001', service: 'sv-hifu', gross: 18_000_000, pay: [['payoo', py(2, 1)]], issued: true },
    { day: 2, hour: 10, customer: 'Chị Lan Demo', phone: '0900000002', service: 'sv-laser', gross: 6_500_000, pay: [['transfer', ft(2, 11)]], issued: true },
    { day: 2, hour: 14, customer: 'Anh Tuấn Demo', phone: '0900000003', service: 'sv-kham', gross: 500_000, pay: [['cash']], issued: true },
    { day: 2, hour: 16, customer: 'Chị Hà Demo', phone: '0900000004', kind: 'deposit', service: 'sv-thermage', gross: 10_000_000, pay: [['vietqr', ft(2, 15)]] },
    { day: 1, hour: 9, customer: 'Chị Ngọc Demo', phone: '0900000005', kind: 'card', service: 'sv-card', gross: 45_000_000, pay: [['installment', py(1, 2)]] },
    { day: 1, hour: 11, customer: 'Chị Thu Demo', phone: '0900000006', service: 'sv-laser', gross: 6_500_000, pay: [['payoo', py(1, 3)]], cancelled: 'Khách đổi ý trước khi làm dịch vụ' },
    { day: 1, hour: 15, customer: 'Chị Vy Demo', phone: '0900000007', service: 'sv-hifu', gross: 18_000_000, pay: [['payoo', py(1, 4)]], buyer: COMPANY_BUYER },
    { day: 1, hour: 17, customer: 'Chị Hạnh Demo', phone: '0900000008', kind: 'deposit', service: 'sv-hifu', gross: 5_000_000, pay: [['transfer', ft(1, 21)]] },
    // Cọc của khách có đơn trên POS: hệ thống gợi ý gộp theo SĐT
    { day: 3, hour: 10, customer: 'Nguyễn Bích Ngọc', phone: '0901234567', kind: 'deposit', service: 'sv-thermage', gross: 5_000_000, pay: [['transfer', ft(3, 41)]] },
    { day: 0, hour: 8, customer: 'Chị Hà Demo', phone: '0900000004', service: 'sv-thermage', gross: 35_000_000, pay: [['payoo', py(0, 5)]], depositOf: 3 },
    { day: 0, hour: 9, customer: 'Chị Oanh Demo', phone: '0900000009', service: 'sv-filler', gross: 9_000_000, pay: [['transfer', ft(0, 31)]] },
    { day: 0, hour: 9, customer: 'Chị Dung Demo', phone: '0900000010', service: 'sv-laser', gross: 6_500_000, pay: [['payoo', '']], buyer: { ...COMPANY_BUYER, name: 'Công ty CP Demo Hoa Sen', taxCode: '' } },
    { day: 0, hour: 10, customer: 'Anh Khoa Demo', phone: '0900000011', service: 'sv-serum', gross: 1_200_000, pay: [['cash']], edited: true },
    // Đã xuất hóa đơn rồi quầy sửa tiền trong ngày: báo Kế toán lập hóa đơn điều chỉnh hoặc thay thế
    { day: 0, hour: 10, customer: 'Chị Trang Demo', phone: '0900000012', service: 'sv-laser', gross: 6_000_000, pay: [['transfer', ft(0, 37)]], issued: true, flag: 'Sửa bill: giảm thêm 500.000 đ theo chương trình tháng' },
    // Thẻ liệu trình: sắp hết hạn, còn nợ, quá hạn, Combo có chiết khấu tổng đơn, hoàn tiền
    { day: 30, hour: 9, customer: 'Chị Quỳnh Demo', phone: '0900000013', kind: 'card', service: 'sv-card', gross: 45_000_000, pay: [['transfer', ft(30, 51)]], issued: true },
    { day: 12, hour: 11, customer: 'Chị Uyên Demo', phone: '0900000014', kind: 'card', service: 'GOI-HIFU5', gross: 60_000_000, paid: 20_000_000, pay: [['transfer', ft(12, 52)]] },
    { day: 40, hour: 14, customer: 'Chị Thảo Demo', phone: '0900000015', kind: 'card', service: 'GOI-TRIMUN10', gross: 10_000_000, pay: [['cash']], issued: true },
    { day: 15, hour: 10, customer: 'Chị Diệp Demo', phone: '0900000016', kind: 'card', service: 'DV-LASER', gross: 13_300_000, pay: [['payoo', py(15, 53)]], issued: true,
      lines: [
        { serviceId: 'DV-LASER', name: 'Laser Pico 5 buổi (trong Combo)', list: 10_000_000, lineDiscount: 1_000_000, orderDiscount: allocateDiscount([9_000_000, 5_000_000], 700_000)[0] },
        { serviceId: 'DV-CSD', name: 'Chăm sóc da 5 buổi (trong Combo)', list: 5_000_000, lineDiscount: 0, orderDiscount: allocateDiscount([9_000_000, 5_000_000], 700_000)[1] },
      ] },
    { day: 20, hour: 15, customer: 'Chị Hoa Demo', phone: '0900000017', kind: 'card', service: 'GOI-TRIMUN10', gross: 10_000_000, pay: [['payoo', py(20, 54)]], issued: true },
    { day: 35, hour: 16, customer: 'Chị Liên Demo', phone: '0900000018', kind: 'card', service: 'GOI-TRIMUN10', gross: 10_000_000, pay: [['transfer', ft(35, 55)]], issued: true, flag: 'Hủy thẻ, hoàn tiền theo hồ sơ đã duyệt' },
    // Cọc theo chương trình đã hết hạn và cọc sắp hết hạn
    { day: 40, hour: 11, customer: 'Chị Phượng Demo', phone: '0900000019', kind: 'deposit', service: 'sv-thermage', gross: 8_000_000, pay: [['transfer', ft(40, 56)]], program: { name: 'CTKM tháng 9', until: dateOf(10) } },
    { day: 25, hour: 11, customer: 'Chị Yến Demo', phone: '0900000020', kind: 'deposit', service: 'sv-hifu', gross: 3_000_000, pay: [['vietqr', ft(25, 57)]] },
    // Phiếu phụ thu độc lập với bill chính, bill thu nhiều hình thức
    { day: 1, hour: 16, customer: 'Chị Vy Demo', phone: '0900000007', kind: 'surcharge', service: SURCHARGE.pain.serviceId, gross: 500_000, pay: [['cash']], parent: 6 },
    { day: 0, hour: 10, customer: 'Chị Oanh Demo', phone: '0900000009', kind: 'surcharge', service: SURCHARGE.test.serviceId, gross: 300_000, pay: [['cash']], parent: 10 },
    { day: 0, hour: 11, customer: 'Chị Mai Demo', phone: '0900000001', service: 'sv-laser', gross: 6_500_000, pay: [['cash']], parts: [['payoo', 3_000_000, py(0, 73)], ['cash', 3_500_000]] },
  ];
  const cardSeeds: CardSeed[] = [
    { bill: 14, pkg: 'GOI-THERMAGE3', name: 'Thẻ Thermage 3 buổi', expiresIn: 12, lines: [['s1', 3, 15_000_000]], uses: [{ day: 20, items: [['s1', 1]], team: TEAM_A, machine: 'MAY-THERMAGE-01' }, { day: 6, items: [['s1', 1]], team: TEAM_A, machine: 'MAY-THERMAGE-01' }] },
    { bill: 15, pkg: 'GOI-HIFU5', name: 'Gói Hifu 5 buổi', expiresIn: 350, lines: [['s2', 5, 12_000_000]], uses: [] },
    { bill: 16, pkg: 'GOI-TRIMUN10', name: 'Gói trị mụn 10 buổi', expiresIn: -5, lines: [['s6', 10, 1_000_000]], uses: [{ day: 35, items: [['s6', 1]], team: TEAM_B }, { day: 30, items: [['s6', 1]], team: TEAM_B }, { day: 25, items: [['s6', 1]], team: TEAM_B }] },
    { bill: 17, pkg: 'COMBO-DEMO', name: 'Combo Laser và chăm sóc da', expiresIn: 200, lines: [['s4', 5, 1_710_000], ['s5', 5, 950_000]], uses: [{ day: 5, items: [['s4', 1], ['s5', 1]], team: TEAM_B, machine: 'MAY-PICO-01' }] },
    { bill: 18, pkg: 'GOI-TRIMUN10', name: 'Gói trị mụn 10 buổi', expiresIn: 220, lines: [['s6', 10, 1_000_000]], uses: [{ day: 15, items: [['s6', 1]], team: TEAM_B }, { day: 8, items: [['s6', 1]], team: TEAM_B }] },
    { bill: 19, pkg: 'GOI-TRIMUN10', name: 'Gói trị mụn 10 buổi', expiresIn: 205, lines: [['s6', 10, 1_000_000]], uses: [{ day: 30, items: [['s6', 1]], team: TEAM_B }] },
  ];

  const demoBills: Omit<Bill, 'seq' | 'no'>[] = seeds.map((x, i) => {
    // Bill hôm nay luôn nằm trước giờ mở demo, dù mở lúc sáng sớm
    const at = x.day
      ? today.subtract(x.day, 'day').hour(x.hour).minute(10 + i * 3).valueOf()
      : Math.max(today.valueOf() + i * 60000, now - (seeds.length - i) * 1200000);
    const sv = ALL_DEMO_SERVICES.find(v => v.id === x.service)!;
    const by = x.by || 'Kế toán quầy';
    const paid = x.paid ?? x.gross;
    const bill: Omit<Bill, 'seq' | 'no'> = {
      id: `kt-${i}`, date: dateOf(x.day), at, customer: x.customer, phone: x.phone || '', branchId: POS_BRANCH, source: 'demo', debt: x.gross - paid, refunds: [], buyer: x.buyer, program: x.program,
      parentBillId: x.parent === undefined ? undefined : `kt-${x.parent}`,
      sale: x.kind === 'surcharge' ? undefined : SALES[i % SALES.length], cskh: x.kind === 'surcharge' ? undefined : CSKH, team: x.kind ? undefined : (i % 2 ? TEAM_A : TEAM_B),
      kind: x.kind || 'service',
      depositOf: x.depositOf === undefined ? undefined : `kt-${x.depositOf}`,
      lines: x.lines
        ? x.lines.map((l, n) => ({ ...l, id: `l-${i}-${n}`, gross: l.list - l.lineDiscount - l.orderDiscount }))
        : [{ id: `l-${i}`, serviceId: sv.id, name: sv.name, gross: x.gross }],
      payments: x.parts
        ? x.parts.map(([method, amount, ref], n) => ({ id: `p-${i}-${n}`, method, amount, ref: ref || '' }))
        : x.pay.map(([method, ref], n) => ({ id: `p-${i}-${n}`, method, amount: paid, ref: ref || '' })),
      status: x.cancelled ? 'cancelled' : 'valid',
      createdBy: by,
      log: [{ at, by, action: 'Tạo bill' }],
      invoice: { status: 'none' },
    };
    if (x.cancelled) bill.log.push({ at: at + 3600000, by, action: 'Hủy bill', reason: x.cancelled });
    if (x.flag) bill.log.push({ at: at + 300000, by, action: 'Sửa bill', reason: x.flag.replace('Sửa bill: ', ''), changes: [{ field: 'Tiền Laser trị nám', from: '6.500.000 ₫', to: '6.000.000 ₫' }] });
    if (x.edited) {
      bill.log.push({
        at: at + 600000, by, action: 'Sửa bill', reason: 'Thu ngân gõ nhầm số tiền',
        changes: [{ field: 'Tiền Serum phục hồi', from: '1.500.000 ₫', to: '1.200.000 ₫' }],
      });
    }
    return bill;
  });

  const txnsOf = (id: string) => pos.transactions.filter(t => t.orderId === id);
  const posBills = pos.orders.filter(o => o.branchId === POS_BRANCH).map((o, i) => {
    const b = billFromOrder(o, txnsOf(o.id), ALL_DEMO_SERVICES, 'pos');
    // Vài bill cố ý chưa gán CSKH để thấy danh sách thiếu nhân sự
    return { ...b, cskh: i % 40 === 7 ? undefined : CSKH, team: b.kind === 'service' ? { ...(i % 2 ? TEAM_A : TEAM_B), ...b.team } : undefined };
  });
  const seedIssued = new Set<string>(seeds.flatMap((x, i) => (x.issued ? [`kt-${i}`] : [])));
  const seedFlag = new Map<string, string>(seeds.flatMap((x, i) => (x.flag ? [[`kt-${i}`, x.flag] as const] : [])));
  const yesterday = dateOf(1);

  // Đánh số liên tục theo thời gian: cả bill POS và bill tình huống đều chung một chuỗi chứng từ
  let invoiceSeq = 356;
  const bills: Bill[] = [...demoBills, ...posBills].sort((a, b) => a.at - b.at).map((b, i) => {
    const seq = 1201 + i;
    const bill: Bill = { ...b, seq, no: billNo(seq) };
    // Ngày đã qua coi như đã ghi đủ mã máy; hôm nay để trống để thấy việc còn phải ghi ca
    if (bill.date < dateOf(0) && bill.kind === 'service') {
      bill.lines = bill.lines.map(l => {
        const machine = MACHINES[ALL_DEMO_SERVICES.find(v => v.id === l.serviceId)?.group || ''];
        return machine ? { ...l, machine } : l;
      });
    }
    // Đơn POS từ hôm kia trở về trước coi như đã xuất hóa đơn để có số liệu kê khai
    const autoIssue = bill.source === 'pos' && bill.date < yesterday && bill.status === 'valid' && bill.kind !== 'deposit';
    if (seedIssued.has(bill.id) || autoIssue) {
      invoiceSeq += 1;
      bill.invoice = { status: 'issued', no: invoiceNo(invoiceSeq), at: bill.at + 7200000 };
      if (bill.refunds.length) bill.invoice = flagInvoice(bill.invoice, 'Hoàn tiền trên POS sau khi đã xuất hóa đơn');
      if (seedFlag.has(bill.id)) bill.invoice = flagInvoice(bill.invoice, seedFlag.get(bill.id));
    }
    return bill;
  });

  const lockAt = (date: string) => dayjs(date).hour(19).valueOf();
  const month = (back: number, day: number) => today.subtract(back, 'month').date(day).format('YYYY-MM-DD');
  const PAY_OF: Record<PayMethod, PaymentMethod> = { cash: 'cash', transfer: 'transfer', vietqr: 'transfer', payoo: 'card', installment: 'card', prepaid: 'prepaid' };

  // Thẻ của bill tình huống; thẻ của đơn POS và thẻ bên Liệu trình do syncCards đọc vào
  const teamOf = new Map<string, { team: Team; machine: string }>();
  const demoCards: TreatmentCard[] = cardSeeds.map((c, n) => {
    const bill = bills.find(b => b.id === `kt-${c.bill}`)!;
    const createdAt = new Date(bill.at).toISOString();
    const card: TreatmentCard = {
      id: `KT-THE-${n + 1}`, customerId: bill.phone, customerName: bill.customer, customerPhone: bill.phone, packageId: c.pkg, packageCode: c.pkg, packageName: c.name,
      category: 'Thẻ demo', kind: 'session', branchId: POS_BRANCH, soldBy: bill.sale || '', consultant: bill.sale || '', createdAt,
      expiresAt: dayjs(today).add(c.expiresIn, 'day').format('YYYY-MM-DD'), listPrice: bill.lines.reduce((t, l) => t + (l.list ?? l.gross), 0), price: bill.lines.reduce((t, l) => t + l.gross, 0),
      lines: c.lines.map(([serviceId, total, unitValue]) => { const t = TREATMENT_SERVICES.find(x => x.id === serviceId); return { serviceId, name: t?.name || serviceId, group: t?.group || '', role: 'main' as const, total, bonus: 0, usedBefore: 0, unitValue }; }),
      faceValue: 0, valueAdjust: 0, valueUsedBefore: 0, allowedServiceIds: [], installments: bill.debt
        ? [{ no: 1, dueDate: bill.date, amount: bill.lines.reduce((t, l) => t + l.gross, 0) - bill.debt }, { no: 2, dueDate: dayjs(bill.date).add(30, 'day').format('YYYY-MM-DD'), amount: bill.debt }]
        : [{ no: 1, dueDate: bill.date, amount: bill.lines.reduce((t, l) => t + l.gross, 0) }],
      payments: bill.payments.map(p => ({ id: `${p.id}-c`, at: createdAt, amount: p.amount, method: PAY_OF[p.method], txnRef: p.ref, by: bill.createdBy, branchId: POS_BRANCH })),
      usages: [], bookings: [], refunds: [], events: [], status: 'active', source: 'pos', orderId: bill.id, note: '', policy: policyOf(undefined),
    };
    c.uses.forEach((u, k) => {
      const id = `kt-use-${n}-${k}`;
      const usage = { id, at: today.subtract(u.day, 'day').hour(14).toISOString(), branchId: POS_BRANCH, staffId: '', items: usageItems(card, u.items.map(([serviceId, qty]) => ({ serviceId, qty }))), by: u.team.ktv || '', note: '', signed: true };
      card.usages.push(usage);
      teamOf.set(id, { team: u.team, machine: u.machine || '' });
    });
    return card;
  });

  const base: KtState = {
    v: 5,
    tier: 'counter',
    rates: ratesFor(ALL_DEMO_SERVICES),
    services: ALL_DEMO_SERVICES,
    bills,
    locks: {},
    lockLog: [],
    payoo: [],
    bank: [],
    invoiceSeq,
    stock: DEMO_STOCK,
    rules: DEFAULT_RULES,
    vouchers: demoVouchers(month),
    inputInvoices: demoInputs(month),
    posAlerts: [],
    posSync: { at: now, orders: posBills.length, serverOrders: 0 },
    cards: demoCards,
    usageLog: [],
    refundCases: [],
    commissionRates: DEMO_COMMISSION_RATES,
    cardGate: 'full',
    depositDays: 30,
    posFeePct: 1.5,
    counterUser: 'kt-thu',
    rushDays: {},
    drafts: demoDrafts(now),
    shifts: [],
    cashFloat: 2_000_000,
  };

  const synced = syncCards(base, {
    treatment: cardInput.treatment || [], packages: cardInput.packages || seedPackages(new Date(now)), cfg: DEFAULT_CONFIG,
    pos: { orders: pos.orders, transactions: pos.transactions, refunds: cardInput.refunds || [], customers: cardInput.customers || SEED_CUSTOMERS },
  }, now);

  // Nhật ký đánh số theo thời gian trên toàn bộ thẻ; Liệu trình chỉ có một nhân viên nên giữ người đó làm KTV
  const usageLog = [...synced.usageLog].sort((a, b) => a.at - b.at).map((e, i) => {
    const seq = i + 1;
    const meta = teamOf.get(e.id);
    return { ...e, seq, no: usageNo(seq), team: meta?.team || e.team, machine: meta?.machine || e.machine };
  });

  const step = (tier: CaseStep['tier'], by: string, back: number, action: string): CaseStep => ({ tier, by, at: today.subtract(back, 'day').hour(10).valueOf(), action });
  const caseOf = (cardId: string, no: number, back: number, reason: string, penaltyPct: number, testFee: number, status: RefundCase['status'], steps: CaseStep[], extra: Partial<RefundCase> = {}): RefundCase => {
    const card = synced.cards.find(c => c.id === cardId)!;
    const q = refundQuoteOf(synced, card, { penaltyPct, testFee });
    return { id: `hs-${no}`, no: `HT-${String(no).padStart(4, '0')}`, cardId, billId: card.orderId, at: today.subtract(back, 'day').hour(9).valueOf(), reason, status, penaltyPct, ...q, steps, ...extra };
  };
  const refundCases = [
    caseOf('KT-THE-6', 1, 9, 'Khách chuyển công tác, xin hủy phần chưa làm', 10, 500_000, 'paid', [step('counter', 'Kế toán quầy', 9, 'Tạo hồ sơ'), step('revenue', 'Kế toán nội bộ', 9, 'Duyệt cấp 1'), step('chief', 'Kế toán trưởng', 8, 'Duyệt cấp 2'), step('revenue', 'Kế toán nội bộ', 8, 'Xác nhận đã chi')], { method: 'transfer', ref: ft(8, 61), paidDate: dateOf(8) }),
    caseOf('KT-THE-5', 2, 2, 'Khách dị ứng sau 2 buổi, bác sĩ khuyên ngừng', 10, 500_000, 'pending_l2', [step('counter', 'Kế toán quầy', 2, 'Tạo hồ sơ'), step('revenue', 'Kế toán nội bộ', 1, 'Duyệt cấp 1')]),
    caseOf('KT-THE-3', 3, 0, 'Thẻ quá hạn, khách xin hoàn phần còn lại', 20, 0, 'pending_l1', [step('counter', 'Kế toán quầy', 0, 'Tạo hồ sơ')]),
  ];
  const cards = synced.cards.map(c => (c.id === 'KT-THE-6' ? { ...c, status: 'refunded' as const } : c));

  const lockDates = Array.from(new Set([...bills.map(b => b.date), ...usageLog.map(e => e.date)])).filter(d => d < dateOf(0));
  return {
    ...synced,
    shifts: demoShifts(bills, [dateOf(2), dateOf(1)], base.cashFloat),
    cards,
    usageLog,
    refundCases,
    locks: Object.fromEntries(lockDates.map(d => [d, { by: 'Kế toán nội bộ', at: lockAt(d) }])),
    lockLog: lockDates.map(d => ({ date: d, action: 'lock' as const, by: 'Kế toán nội bộ', at: lockAt(d) })),
  };
}

/** Tạm tính đang chờ khách quyết định: chưa có số bill */
function demoDrafts(now: number): BillDraft[] {
  const base = { by: 'Thu Demo (Kế toán quầy)', kind: 'service' as const, status: 'draft' as const, note: '', payments: [] };
  return [
    { ...base, id: 'tt-2', no: 'TT-0002', at: now - 1800000, updatedAt: now - 1800000, customer: 'Chị Hồng Demo', phone: '0900000022', sale: SALES[1], orderDiscount: 0,
      lines: [{ id: 'tl-3', serviceId: 'sv-kham', name: 'Khám da liễu', list: 500_000, lineDiscount: 0 }] },
    { ...base, id: 'tt-1', no: 'TT-0001', at: now - 3600000, updatedAt: now - 3600000, customer: 'Chị Bình Demo', phone: '0900000021', sale: SALES[0], orderDiscount: 700_000,
      lines: [{ id: 'tl-1', serviceId: 'sv-laser', name: 'Laser trị nám', list: 6_500_000, lineDiscount: 500_000 }, { id: 'tl-2', serviceId: 'sv-serum', name: 'Serum phục hồi', list: 1_500_000, lineDiscount: 0 }] },
  ];
}

/** Hai ngày gần nhất đã chốt 2 ca và Kế toán nội bộ đã nhận; một ca có lệch tiền mặt để thấy lý do */
function demoShifts(bills: Bill[], dates: string[], float: number): Shift[] {
  const txns = pendingTxns({ bills, shifts: [], locks: {}, lockLog: [] });
  let n = 0;
  const shifts = dates.flatMap(date => [{ name: 'Ca sáng', hour: 8, morning: true }, { name: 'Ca chiều', hour: 14, morning: false }].flatMap(c => {
    const list = txns.filter(t => t.date === date && (dayjs(bills.find(b => b.id === t.billId)!.at).hour() < 14) === c.morning);
    if (!list.length) return [];
    n += 1;
    const at = dayjs(date).hour(c.morning ? 14 : 19).valueOf();
    const cashExpected = float + list.filter(t => t.method === 'cash').reduce((a, t) => a + t.amount, 0);
    const lech = date === dates[dates.length - 1] && !c.morning ? -200_000 : 0;
    return [{
      id: `ca-${n}`, no: `CA-${String(n).padStart(5, '0')}`, date, name: c.name, by: 'Thu Demo (Kế toán quầy)', at, openingCash: float, txns: list,
      cashExpected, cashCounted: cashExpected + lech, diff: lech, diffReason: lech ? 'Thối nhầm cho khách, đã báo Kế toán nội bộ' : '', status: 'received' as const, receivedBy: 'Kế toán nội bộ', receivedAt: at + 3600000,
    }];
  }));
  return shifts.reverse();
}

function demoVouchers(month: (back: number, day: number) => string): Voucher[] {
  const v = (n: number, date: string, ruleId: string, desc: string, amount: number, scope: Voucher['scope'], reason: string, costCenter: string, nonDeductible?: string): Voucher => ({
    id: `ct-${n}`, no: `CT-${String(n).padStart(4, '0')}`, date, ruleId, desc, amount, scope, reason, costCenter, branchId: POS_BRANCH, by: 'Kế toán tổng hợp', at: dayjs(date).hour(17).valueOf(), nonDeductible,
  });
  return [1, 0].flatMap((back, k) => {
    const d = month(back, 28 - k * 25);
    const n = k * 10;
    return [
      v(n + 1, d, 'dep_straight', 'Khấu hao máy Thermage: đường thẳng 60 tháng', 40_000_000, 'tax', 'Sổ Thuế tính đường thẳng theo khung', 'TTCP-MAY'),
      v(n + 2, d, 'dep_shot', 'Khấu hao máy Thermage: 1.100 shot thực tế', 55_000_000, 'mgmt', 'Sổ Quản trị tính theo số shot vận hành', 'TTCP-MAY'),
      v(n + 3, d, 'alloc_tax', 'Phân bổ công cụ dụng cụ: 12 kỳ', 10_000_000, 'tax', 'Phân bổ theo kỳ thuế', 'TTCP-QL'),
      v(n + 4, d, 'alloc_mgmt', 'Phân bổ công cụ dụng cụ: 24 kỳ', 5_000_000, 'mgmt', 'Phân bổ theo thời gian dùng thực tế', 'TTCP-QL'),
      v(n + 5, d, 'expense', 'Chi tiếp khách không có hóa đơn', 3_500_000, 'both', '', 'TTCP-QL', 'Không có hóa đơn hợp lệ'),
    ];
  });
}

function demoInputs(month: (back: number, day: number) => string): InputInvoice[] {
  const i = (n: number, date: string, seller: string, taxCode: string, net: number, rate: number, debit: string, pay: InputInvoice['pay'], payRef: string, desc: string): InputInvoice => ({
    id: `hdv-${n}`, no: `00${1000 + n}`, seller, taxCode, date, net, vat: Math.round((net * rate) / 100), rate, debit, pay, payRef, desc,
  });
  return [
    i(1, month(1, 5), 'Công ty Demo Thiết bị Y tế', '0300000001', 120_000_000, 8, '152', 'bank', 'FT-DEMO-01', 'Tip Thermage'),
    i(2, month(1, 12), 'Công ty Demo Dược Mỹ phẩm', '0300000002', 45_000_000, 8, '1561', 'bank', 'FT-DEMO-02', 'Serum, kem chống nắng'),
    i(3, month(1, 18), 'Công ty Demo Vệ sinh', '0300000003', 6_000_000, 8, '6428', 'cash', '', 'Dịch vụ vệ sinh tháng'),
    i(4, month(1, 22), 'Cửa hàng Demo Văn phòng phẩm', '0300000004', 3_000_000, 8, '6428', 'cash', '', 'Văn phòng phẩm lần 1'),
    i(5, month(1, 22), 'Cửa hàng Demo Văn phòng phẩm', '0300000004', 2_500_000, 8, '6428', 'cash', '', 'Văn phòng phẩm lần 2'),
    i(6, month(0, 2), 'Công ty Demo Thiết bị Y tế', '0300000001', 60_000_000, 8, '152', 'none', '', 'Cartridge Hifu'),
    i(7, month(0, 2), 'Công ty Demo In ấn', '0300000005', 1_800_000, 8, '6428', 'cash', '', 'In tờ rơi'),
    i(8, month(0, 3), 'Công ty Demo Dược Mỹ phẩm', '0300000002', 30_000_000, 8, '1561', 'bank', '', 'Mặt nạ phục hồi'),
  ];
}

const feeOf = (gross: number, pct: number) => Math.round((gross * pct) / 100);

/**
 * File mẫu Payoo sinh từ bill đang có, cố ý gài 1 dòng lệch tiền, 1 dòng không có bill
 * và bỏ sót 1 giao dịch để thấy đủ các trạng thái.
 */
export function samplePayoo(s: Pick<KtState, 'bills'>): PayooRow[] {
  const pays = s.bills.filter(b => b.status === 'valid').flatMap(b => b.payments.filter(p => (p.method === 'payoo' || p.method === 'installment') && p.ref).map(p => ({ b, p })));
  const rows: PayooRow[] = pays.slice(0, -1).map(({ b, p }, i) => {
    const gross = i === 1 ? p.amount - 200_000 : p.amount;
    const fee = feeOf(gross, p.method === 'installment' ? 3 : 1.5);
    return { ref: p.ref, gross, fee, net: gross - fee, date: b.date };
  });
  const last = pays.at(-1);
  if (last) rows.push({ ref: 'PY000000000', gross: 2_000_000, fee: 30_000, net: 1_970_000, date: last.b.date });
  return rows;
}

/** Sao kê mẫu: nội dung chuyển khoản viết nhiều kiểu như thực tế */
export function sampleBank(s: Pick<KtState, 'bills'>): BankRow[] {
  const pays = s.bills.filter(b => b.status === 'valid').flatMap(b => b.payments.filter(p => p.method === 'transfer' || p.method === 'vietqr').map(p => ({ b, p })));
  const rows: BankRow[] = pays.map(({ b, p }, i) => ({
    date: b.date,
    amount: p.amount,
    desc: i % 2 ? `${b.customer} CK thanh toan don ${b.no}` : `MBVCB.${p.ref}.${b.customer} chuyen tien`,
  }));
  if (rows[0]) rows.push({ date: rows[0].date, amount: 3_000_000, desc: 'Chuyen khoan khong ghi noi dung' });
  return rows;
}
